import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { AuthContext } from "context/AuthContext";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import { showToast } from "components/ui/toast";
import MfaEnrolment from "components/mfa/MfaEnrolment";
import {
  useRegenerateBackupCodesMutation,
  useSessionsQuery,
  useSignOutEverywhereMutation,
  useUpdatePasswordMutation,
} from "apis/auth";

const ROLE_LABELS = {
  superAdmin: "Super Admin",
  beleefAdmin: "Beleef Admin",
  meloAdmin: "Melo Admin",
  admin: "Admin",
};

const Card = ({ title, description, children }) => (
  <section className="border border-mediumgray rounded-md p-6 mb-6">
    <h5 className="mb-1">{title}</h5>
    {description && (
      <p className="text-sm text-darkergray mb-4">{description}</p>
    )}
    {children}
  </section>
);

const Pill = ({ tone, children }) => (
  <span
    className={`inline-block px-2 py-1 rounded text-xs font-medium ${
      tone === "on"
        ? "bg-green-50 text-green-700 border border-green-200"
        : "bg-amber-50 text-amber-700 border border-amber-200"
    }`}
  >
    {children}
  </span>
);

const formatWhen = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

// ── Two-factor card ────────────────────────────────────────────────────────
// The whole point of this page for an existing admin: somewhere to switch MFA
// on before it becomes mandatory.
const TwoFactorCard = () => {
  const { user, refresh } = useContext(AuthContext);
  const [enrolling, setEnrolling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [newCodes, setNewCodes] = useState(null);

  const { mutateAsync: regenerate, isPending: regeneratingCodes } =
    useRegenerateBackupCodesMutation();
  const passwordForm = useForm({ defaultValues: { password: "" } });

  const enabled = Boolean(user?.twoFactorEnabled);

  const onRegenerate = async ({ password }) => {
    try {
      const res = await regenerate(password);
      setNewCodes(res.data.backupCodes);
      passwordForm.reset();
    } catch {
      // toasted by the mutation
    }
  };

  return (
    <Card
      title="Two-factor authentication"
      description="A 6-digit code from your phone, on top of your password."
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Pill tone={enabled ? "on" : "off"}>
          {enabled ? "Enabled" : "Not enabled"}
        </Pill>

        {enabled ? (
          <button
            type="button"
            className="text-sm text-blue-600 hover:underline"
            onClick={() => {
              setRegenerating(true);
              setNewCodes(null);
            }}
          >
            Regenerate backup codes
          </button>
        ) : (
          <Button
            type="button"
            className="black-button"
            onClick={() => setEnrolling(true)}
          >
            Enable two-factor authentication
          </Button>
        )}
      </div>

      {enabled && (
        <p className="text-sm text-darkergray mt-4">
          To turn two-factor off — for example if you've lost your phone — ask a
          super admin to reset it for you. It can't be switched off from here.
        </p>
      )}

      <Modal
        isOpen={enrolling}
        onClose={() => setEnrolling(false)}
        title="Set up two-factor authentication"
        className="max-w-md"
      >
        <MfaEnrolment
          onCancel={() => setEnrolling(false)}
          onComplete={async () => {
            setEnrolling(false);
            await refresh();
            showToast("success", "Two-factor authentication is on");
          }}
        />
      </Modal>

      <Modal
        isOpen={regenerating}
        onClose={() => {
          setRegenerating(false);
          setNewCodes(null);
          passwordForm.reset();
        }}
        title="Regenerate backup codes"
        className="max-w-md"
      >
        {newCodes ? (
          <div className="space-y-4 p-2">
            <p className="text-sm text-darkergray">
              Your old codes no longer work. Save these — they're shown once.
            </p>
            <div className="grid grid-cols-2 gap-2 p-4 bg-lightergray rounded-md">
              {newCodes.map((code) => (
                <code key={code} className="text-sm tracking-wider">
                  {code}
                </code>
              ))}
            </div>
            <Button
              type="button"
              className="w-full black-button"
              onClick={() => {
                setRegenerating(false);
                setNewCodes(null);
              }}
            >
              Done
            </Button>
          </div>
        ) : (
          <form
            onSubmit={passwordForm.handleSubmit(onRegenerate)}
            className="space-y-4 p-2"
          >
            <p className="text-sm text-darkergray">
              This invalidates your existing backup codes. Confirm your password
              to continue.
            </p>
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Your password"
              className="form-input border border-mediumgray"
              {...passwordForm.register("password", { required: true })}
            />
            <Button
              type="submit"
              className="w-full black-button"
              loading={regeneratingCodes}
              disabled={regeneratingCodes}
            >
              Generate new codes
            </Button>
          </form>
        )}
      </Modal>
    </Card>
  );
};

// ── Password card ──────────────────────────────────────────────────────────
const PasswordCard = () => {
  const { mutate: updatePassword, isPending } = useUpdatePasswordMutation();
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: { oldPassword: "", newPassword: "", confirmPassword: "" },
  });

  return (
    <Card
      title="Password"
      description="Changing it signs you out everywhere else."
    >
      <form
        onSubmit={handleSubmit((data) => updatePassword(data))}
        className="space-y-4 max-w-md"
      >
        <div>
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Current password"
            className={`form-input border ${
              errors.oldPassword ? "border-red-500" : "border-mediumgray"
            }`}
            {...register("oldPassword", {
              required: "Current password is required",
            })}
          />
          {errors.oldPassword && (
            <p className="form-error-message">{errors.oldPassword.message}</p>
          )}
        </div>
        <div>
          <input
            type="password"
            autoComplete="new-password"
            placeholder="New password"
            className={`form-input border ${
              errors.newPassword ? "border-red-500" : "border-mediumgray"
            }`}
            {...register("newPassword", {
              required: "New password is required",
              minLength: {
                value: 8,
                message: "Password must be at least 8 characters",
              },
              pattern: {
                value: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
                message:
                  "Needs at least one number, one uppercase and one lowercase letter",
              },
            })}
          />
          {errors.newPassword && (
            <p className="form-error-message">{errors.newPassword.message}</p>
          )}
        </div>
        <div>
          <input
            type="password"
            autoComplete="new-password"
            placeholder="Confirm new password"
            className={`form-input border ${
              errors.confirmPassword ? "border-red-500" : "border-mediumgray"
            }`}
            {...register("confirmPassword", {
              required: "Confirm your new password",
              validate: (value) =>
                value === getValues("newPassword") ||
                "The passwords do not match",
            })}
          />
          {errors.confirmPassword && (
            <p className="form-error-message">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>
        <Button
          type="submit"
          className="black-button"
          loading={isPending}
          disabled={isPending}
        >
          Change password
        </Button>
      </form>
    </Card>
  );
};

// ── Sessions card ──────────────────────────────────────────────────────────
// First UI for logoutFromAllDevices, which the backend has had all along with
// nothing calling it.
const SessionsCard = () => {
  const { sessions, isPending } = useSessionsQuery();
  const { mutate: signOutEverywhere, isPending: signingOut } =
    useSignOutEverywhereMutation();

  return (
    <Card
      title="Active sessions"
      description="Where your account is currently signed in."
    >
      {isPending ? (
        <p className="text-sm text-darkergray">Loading…</p>
      ) : sessions.length === 0 ? (
        <p className="text-sm text-darkergray">No other sessions.</p>
      ) : (
        <ul className="divide-y divide-mediumgray mb-4">
          {sessions.map((session) => (
            <li key={session.id} className="py-3 flex justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm mb-0 truncate">
                  {session.userAgent || "Unknown device"}
                  {session.current && (
                    <span className="ml-2 text-xs text-green-700">
                      this device
                    </span>
                  )}
                </p>
                <p className="text-xs text-darkergray mb-0">
                  {session.ipAddress || "unknown IP"} · signed in{" "}
                  {formatWhen(session.createdAt)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Button
        type="button"
        className="black-button"
        loading={signingOut}
        disabled={signingOut}
        onClick={() => signOutEverywhere()}
      >
        Sign out everywhere
      </Button>
    </Card>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────
const Settings = () => {
  const { user } = useContext(AuthContext);

  return (
    <div className="container mx-auto max-w-3xl py-8 px-4">
      <h4 className="mb-6">Settings</h4>

      <Card title="Account">
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <dt className="text-darkergray mb-1">Name</dt>
            <dd className="mb-0">{user?.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-darkergray mb-1">Email</dt>
            <dd className="mb-0 break-all">{user?.email || "—"}</dd>
          </div>
          <div>
            <dt className="text-darkergray mb-1">Role</dt>
            <dd className="mb-0">{ROLE_LABELS[user?.role] || user?.role || "—"}</dd>
          </div>
        </dl>
      </Card>

      <TwoFactorCard />
      <PasswordCard />
      <SessionsCard />
    </div>
  );
};

export default Settings;
